import { useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { getKpiAlertsHistory, getKpiAlertsHistoryForRan, getSeverities } from '@api/kpiAlerts';

import { optionsFormatted } from '@utils/formatter';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IItemsResponse } from '@interfaces/common/response';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IGetKpiAlertsHistoryParamsDTO,
	IKpiAlertHistoryOutDTO,
	IKpiAlertSeverityOutDTO,
} from '@interfaces/kpi/dto/kpiAlertsDTO';

import { ESnackbarTypes, EVendorsValues } from '@enums/common';
import { TSeverity } from '@enums/kpi-common';

import { GET_SEVERITIES, KPI_ALERTS_HISTORY } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseKpiAlertsHistoryResponse {
	kpiAlertsHistory?: IKpiAlertHistoryOutDTO[];
	countOfRecords?: number;
	isLoading?: boolean;
	severities: TSelectOption<TSeverity, IKpiAlertSeverityOutDTO>[];
}

export function useKpiAlertsHistory({
	page,
	pageSize,
	from,
	name,
	fromText,
	isSent,
	isActive,
	organizationId,
	messageBody,
	severity,
	searchValue,
	description,
	sortFieldName,
	sortDirection,
	vendor,
}: IGetKpiAlertsHistoryParamsDTO): IUseKpiAlertsHistoryResponse {
	const dispatch = useAppDispatch();

	const getKpiAlertsHistoryFn = vendor === EVendorsValues.oran
		? getKpiAlertsHistoryForRan
		: getKpiAlertsHistory;

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery<IItemsResponse<IKpiAlertHistoryOutDTO>>({
		queryKey: [
			KPI_ALERTS_HISTORY,
			page,
			pageSize,
			from,
			name,
			fromText,
			isSent,
			isActive,
			organizationId,
			messageBody,
			severity,
			searchValue,
			description,
			sortFieldName,
			sortDirection,
			vendor,
		],
		queryFn: () => getKpiAlertsHistoryFn({
			page,
			pageSize,
			from: from ? startOfDayInUTC(new Date(from)) : undefined,
			name,
			fromText,
			isSent,
			isActive,
			organizationId,
			messageBody,
			severity,
			searchValue,
			description,
			sortFieldName,
			sortDirection,
		}),
		onError: () => {
			dispatch(setSnackbar({
				type: ESnackbarTypes.error,
				message: responseMessages.generalError,
			}));
		},
	});

	const {
		data: severities,
	} = useQuery<IKpiAlertSeverityOutDTO[]>({
		queryKey: [GET_SEVERITIES],
		queryFn: () => getSeverities(),
		onError: () => {
			dispatch(setSnackbar({
				type: ESnackbarTypes.error,
				message: responseMessages.generalError,
			}));
		},
		select: (data: IKpiAlertSeverityOutDTO[]) => optionsFormatted<IKpiAlertSeverityOutDTO>(data),
	});

	return {
		kpiAlertsHistory: data?.items || [],
		countOfRecords: data?.count || 0,
		isLoading: isLoading || isFetching,
		severities: severities || [],
	};
}
